import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Sparkles, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ OPTIONS ============
const INDUSTRIES = [
  "Trades / Construction / HVAC",
  "Logistics / Field Service",
  "Hospitality / Restaurant",
  "Clinic / Medical / Wellness",
  "Retail / E-commerce",
  "B2B / Wholesale",
  "Professional Services / Consulting",
  "Non-profit / Community / Public sector",
  "Other",
];
const TEAM_SIZES = ["Solo (1)", "Small (2-5)", "Medium (6-20)", "Growing (21-50)", "Established (50+)"];
const YEARS = ["< 1 year", "1-3 years", "3-7 years", "7-15 years", "15+ years"];

const COMMS_OPTIONS = ["Group text / SMS", "Email", "WhatsApp", "Slack", "Teams / Zoom chat", "Phone calls only", "Other"];
const SCHED_OPTIONS = ["Google Calendar", "Outlook / M365", "Calendly", "Acuity / Square", "Paper calendar", "None", "Other"];
const INV_OPTIONS = ["QuickBooks", "Stripe", "Square", "PayPal", "Xero / FreshBooks", "Paper invoices", "Other"];
const CRM_OPTIONS = ["Salesforce", "HubSpot", "Pipedrive / Zoho", "Spreadsheet", "Paper / notebook", "None (memory)", "Other"];
const INV_TOOLS = ["Dedicated software", "Spreadsheet", "Paper", "None / by feel", "Other"];
const REPORT_OPTIONS = ["Dashboard tool (Metabase, Looker...)", "Spreadsheet exports", "Accounting software reports", "None", "Other"];

const FREQ_OPTIONS = ["Daily", "Multiple times per week", "Weekly", "Monthly", "Occasional but painful"];
const KPI_OPTIONS = [
  "Revenue growth",
  "Cost reduction",
  "Time / hours saved",
  "Customer retention",
  "Team efficiency",
  "Compliance / audit posture",
  "Brand / customer experience",
  "Faster cash flow",
];
const TIMELINES = ["ASAP (this month)", "1-3 months", "3-6 months", "Just exploring"];
const BUDGET_TIERS = [
  "Tier 1 · Single-Problem Fix ($3.5K–$5K)",
  "Tier 2 · Core Ecosystem ($8.5K–$12K)",
  "Not sure yet — recommend for me",
];
const ENGAGEMENT_OPTIONS = [
  "Full ecosystem build",
  "Single-problem fix first",
  "Retainer / managed only",
  "Consultation first",
];
const CALL_TIMES = ["Weekday mornings", "Weekday afternoons", "Weekday evenings", "Weekends", "Flexible"];

const STEPS = [
  { key: "identity", label: "Identity" },
  { key: "stack", label: "Stack" },
  { key: "friction", label: "Friction" },
  { key: "goals", label: "Goals" },
  { key: "contact", label: "Contact" },
];

const initial = {
  // Step 1
  business_name: "",
  industry: "",
  team_size: "",
  years_in_business: "",
  website: "",
  service_area: "",
  // Step 2
  comms_tools: [],
  scheduling_tools: [],
  invoicing_tools: [],
  crm_tools: [],
  inventory_tools: [],
  reporting_tools: [],
  tech_frustrations: "",
  // Step 3
  bottleneck: "",
  secondary_bottleneck: "",
  data_leakage: "",
  bottleneck_frequency: "",
  revenue_impact: "",
  // Step 4
  success_90_days: "",
  key_kpis: [],
  growth_goals_12mo: "",
  required_integrations: "",
  // Step 5
  timeline: "",
  budget_tier: "",
  preferred_engagement: "",
  best_call_time: "",
  full_name: "",
  email: "",
  phone: "",
};

const stepVariants = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0, transition: { duration: 0.45, ease: [0.19, 1, 0.22, 1] } },
  exit: { opacity: 0, x: -40, transition: { duration: 0.3 } },
};

// ============ REUSABLE CONTROLS ============
const ButtonGrid = ({ options, value, onChange, testPrefix, columns = "grid-cols-2" }) => (
  <div className={`grid ${columns} gap-2`}>
    {options.map((opt) => (
      <button
        key={opt}
        type="button"
        data-testid={`${testPrefix}-${opt.split(" ")[0].toLowerCase().replace(/[^a-z0-9]/g, "")}`}
        onClick={() => onChange(opt)}
        className={`text-left px-4 py-3 text-sm border transition-colors ${
          value === opt
            ? "border-nex-gold bg-nex-gold/10 text-nex-gold"
            : "border-white/10 text-nex-silver hover:border-white/30"
        }`}
      >
        {opt}
      </button>
    ))}
  </div>
);

const MultiChip = ({ options, value, onToggle, testPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const active = value.includes(opt);
      return (
        <button
          key={opt}
          type="button"
          data-testid={`${testPrefix}-${opt.split(" ")[0].toLowerCase().replace(/[^a-z0-9]/g, "")}`}
          onClick={() => onToggle(opt)}
          className={`inline-flex items-center gap-2 px-3 py-2 text-[13px] border transition-colors ${
            active
              ? "border-nex-gold text-nex-gold bg-nex-gold/10"
              : "border-white/10 text-nex-silver hover:border-white/30"
          }`}
        >
          {active && <Check size={12} />}
          {opt}
        </button>
      );
    })}
  </div>
);

const StackRow = ({ label, options, value, onToggle, testKey }) => (
  <div>
    <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-2">{label}</div>
    <MultiChip options={options} value={value} onToggle={onToggle} testPrefix={`stack-${testKey}`} />
  </div>
);

// ============ MAIN ============
const FrictionAudit = ({ embedded = false, onDone, demoMode = false } = {}) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setValue = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIn = (k) => (opt) =>
    setForm((f) => ({ ...f, [k]: f[k].includes(opt) ? f[k].filter((x) => x !== opt) : [...f[k], opt] }));

  const canAdvance = useMemo(() => {
    if (step === 0) return form.business_name.trim() && form.industry && form.team_size;
    if (step === 1) return true; // tech stack is optional but recommended
    if (step === 2) return form.bottleneck.trim().length >= 15;
    if (step === 3) return true; // goals optional but recommended
    if (step === 4)
      return form.timeline && form.full_name.trim() && /.+@.+\..+/.test(form.email);
    return false;
  }, [step, form]);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));
  const progressPct = ((step + 1) / STEPS.length) * 100;

  const submit = async () => {
    setSubmitting(true);
    // Demo mode: fake the round-trip so a public prospect can walk the full
    // 5-step wizard and see the result screen without spamming your admin.
    if (demoMode) {
      await new Promise((r) => setTimeout(r, 900));
      setResult({
        friction_score: 82,
        recommended_engine: "flow",
        deploy_estimate_days: 14,
        preview: true,
      });
      toast.success("Demo · Your brief is preview-only. Book a real audit below.");
      setSubmitting(false);
      return;
    }
    try {
      const { data } = await axios.post(`${API}/friction-audit`, form);
      setResult(data);
      toast.success("Engine Brief received. Check your email for next steps.");
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const detail = Array.isArray(raw)
        ? raw.map((e) => e.msg || JSON.stringify(e)).join(" · ")
        : raw || err.message || "Submission failed";
      toast.error(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setForm(initial);
    setResult(null);
    setStep(0);
  };

  // ============ RESULT SCREEN ============
  if (result) {
    const ResultOuter = embedded ? "div" : "section";
    return (
      <ResultOuter
        id={embedded ? undefined : "friction-audit"}
        data-testid="friction-audit-section"
        className={embedded ? "" : "relative py-28 lg:py-36 bg-[#0A0F1C]"}
      >
        <div className={embedded ? "px-5 py-6" : "max-w-3xl mx-auto px-6"}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="card-industrial corner-tick p-10 md:p-14 text-center"
          >
            <CheckCircle2 size={40} className="text-nex-gold mx-auto mb-6" />
            <div className="mono-label mb-3">// PRELIMINARY ENGINE BRIEF · RECEIVED</div>
            <h2 className="serif-head text-3xl md:text-5xl mb-6">
              Your brief is in. William is on it.
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 my-10">
              <div className="border border-nex-gold/50 p-6 bg-nex-gold/5">
                <div className="mono-label mb-2">Recommended Engine</div>
                <div className="serif-head text-3xl gold-text">{result.engine_recommendation}</div>
              </div>
              <div className="border border-white/10 p-6">
                <div className="mono-label mb-2">Friction Score</div>
                <div className="serif-head text-3xl text-nex-silver">
                  {result.friction_score}
                  <span className="text-nex-chrome text-xl">/100</span>
                </div>
              </div>
            </div>

            <div className="text-left mono-body text-[12px] leading-relaxed border-l-2 border-nex-gold/50 pl-5 mb-8 space-y-3">
              <div><span className="text-nex-gold">01.</span> William personally reviews your Engine Brief within one business day.</div>
              <div><span className="text-nex-gold">02.</span> If it&apos;s a fit, you receive an <span className="text-nex-silver">Acceptance & Consultation Fee</span> link via email.</div>
              <div><span className="text-nex-gold">03.</span> Once paid, we begin your <span className="text-nex-silver">48-hour prototype build</span> — you&apos;ll get a private preview link to watch it come to life.</div>
              <div><span className="text-nex-gold">04.</span> Deploy your full engine in 14 days.</div>
            </div>

            <p className="text-nex-chrome text-sm mb-2">
              Confirmation sent to <span className="text-nex-silver">{form.email}</span>.
            </p>
            {result.email_sent && (
              <p className="mono-body text-[11px] mb-8">✓ Owner alert dispatched from hello@theexchangealliance.org</p>
            )}

            <button onClick={embedded && onDone ? onDone : restart} data-testid="restart-audit-btn" className="tick-btn-secondary mx-auto">
              {embedded && onDone ? "Close" : "Submit another brief"}
            </button>
          </motion.div>
        </div>
      </ResultOuter>
    );
  }

  // ============ MULTI-STEP FORM ============
  const FormOuter = embedded ? "div" : "section";
  return (
    <FormOuter
      id={embedded ? undefined : "friction-audit"}
      data-testid="friction-audit-section"
      className={embedded ? "" : "relative py-28 lg:py-36 bg-[#0A0F1C]"}
    >
      <div className={embedded ? "px-5 py-5" : "max-w-5xl mx-auto px-6"}>
        {!embedded && (
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-8 bg-nex-gold" />
            <span className="mono-label">// The Friction Audit · Full Engine Brief</span>
            <div className="h-px w-8 bg-nex-gold" />
          </div>
          <h2 className="serif-head text-4xl md:text-6xl mb-6">
            What is your team <span className="italic text-nex-gold">screwing up on paper?</span>
          </h2>
          <p className="text-nex-chrome text-lg leading-relaxed">
            Five short steps. ~5 minutes. We capture everything we need to match you to an engine and start your prototype the moment we&apos;re aligned.
          </p>
        </div>
        )}

        {/* Step indicator + progress */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2 md:gap-3 mb-4">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex items-center">
                <div
                  data-testid={`step-indicator-${i}`}
                  className={`flex items-center gap-2 px-3 md:px-4 py-2 border transition-colors ${
                    i === step
                      ? "border-nex-gold text-nex-gold bg-nex-gold/5"
                      : i < step
                      ? "border-nex-gold/40 text-nex-chrome"
                      : "border-white/10 text-nex-muted"
                  }`}
                >
                  <span className="mono-body text-[10px] font-bold">0{i + 1}</span>
                  <span className="mono-body text-[10px] uppercase tracking-widest hidden md:inline">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className="w-4 md:w-6 h-px bg-white/10" />}
              </div>
            ))}
          </div>
          <div className="max-w-2xl mx-auto h-px bg-white/10 relative overflow-hidden">
            <motion.div
              initial={false}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
              className="absolute inset-y-0 left-0 bg-nex-gold"
            />
          </div>
        </div>

        {/* Form card */}
        <div className="card-industrial corner-tick p-6 md:p-10 relative overflow-hidden">
          <div className="min-h-[440px]">
            <AnimatePresence mode="wait">
              {/* ============ STEP 1 · IDENTITY ============ */}
              {step === 0 && (
                <motion.div key="s0" variants={stepVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
                  <div>
                    <label className="mono-label block mb-3">01 · Business Name</label>
                    <input
                      data-testid="input-business-name"
                      type="text"
                      value={form.business_name}
                      onChange={update("business_name")}
                      placeholder="e.g. Wired SC"
                      className="input-industrial"
                    />
                  </div>
                  <div>
                    <label className="mono-label block mb-3">02 · Industry</label>
                    <ButtonGrid
                      options={INDUSTRIES}
                      value={form.industry}
                      onChange={(v) => setValue("industry", v)}
                      testPrefix="industry"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="mono-label block mb-3">03 · Team Size</label>
                      <div className="flex flex-wrap gap-2">
                        {TEAM_SIZES.map((ts) => (
                          <button
                            key={ts}
                            type="button"
                            data-testid={`team-${ts.split(" ")[0].toLowerCase()}`}
                            onClick={() => setValue("team_size", ts)}
                            className={`px-3 py-2 text-sm border transition-colors ${
                              form.team_size === ts
                                ? "border-nex-gold text-nex-gold bg-nex-gold/10"
                                : "border-white/10 text-nex-silver hover:border-white/30"
                            }`}
                          >
                            {ts}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="mono-label block mb-3">04 · Years in Business <span className="text-nex-muted">(optional)</span></label>
                      <div className="flex flex-wrap gap-2">
                        {YEARS.map((y) => (
                          <button
                            key={y}
                            type="button"
                            data-testid={`years-${y.split(" ")[0].replace(/[^a-z0-9]/g, "")}`}
                            onClick={() => setValue("years_in_business", y)}
                            className={`px-3 py-2 text-sm border transition-colors ${
                              form.years_in_business === y
                                ? "border-nex-gold text-nex-gold bg-nex-gold/10"
                                : "border-white/10 text-nex-silver hover:border-white/30"
                            }`}
                          >
                            {y}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="mono-label block mb-3">05 · Website <span className="text-nex-muted">(optional)</span></label>
                      <input
                        data-testid="input-website"
                        type="text"
                        value={form.website}
                        onChange={update("website")}
                        placeholder="yourbusiness.com"
                        className="input-industrial"
                      />
                    </div>
                    <div>
                      <label className="mono-label block mb-3">06 · Service Area <span className="text-nex-muted">(optional)</span></label>
                      <input
                        data-testid="input-service-area"
                        type="text"
                        value={form.service_area}
                        onChange={update("service_area")}
                        placeholder="e.g. Charleston SC + Lowcountry"
                        className="input-industrial"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ============ STEP 2 · TECH STACK ============ */}
              {step === 1 && (
                <motion.div key="s1" variants={stepVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
                  <p className="text-nex-chrome text-sm leading-relaxed border-l-2 border-nex-gold/50 pl-4">
                    Check anything you currently use — it doesn&apos;t have to be perfect. This tells William what to replace, what to integrate with, and what to migrate.
                  </p>
                  <StackRow label="Communication (field ↔ shop ↔ customer)" options={COMMS_OPTIONS} value={form.comms_tools} onToggle={toggleIn("comms_tools")} testKey="comms" />
                  <StackRow label="Scheduling / Calendar" options={SCHED_OPTIONS} value={form.scheduling_tools} onToggle={toggleIn("scheduling_tools")} testKey="sched" />
                  <StackRow label="Invoicing / Payments" options={INV_OPTIONS} value={form.invoicing_tools} onToggle={toggleIn("invoicing_tools")} testKey="invoicing" />
                  <StackRow label="Customer Records / CRM" options={CRM_OPTIONS} value={form.crm_tools} onToggle={toggleIn("crm_tools")} testKey="crm" />
                  <StackRow label="Inventory / Asset Tracking" options={INV_TOOLS} value={form.inventory_tools} onToggle={toggleIn("inventory_tools")} testKey="inventory" />
                  <StackRow label="Reporting / Analytics" options={REPORT_OPTIONS} value={form.reporting_tools} onToggle={toggleIn("reporting_tools")} testKey="reporting" />
                  <div>
                    <label className="mono-label block mb-3">// What frustrates you most about your current stack? <span className="text-nex-muted">(optional)</span></label>
                    <textarea
                      data-testid="input-tech-frustrations"
                      value={form.tech_frustrations}
                      onChange={update("tech_frustrations")}
                      rows={3}
                      placeholder="e.g. QuickBooks doesn&apos;t talk to our field app, we retype everything, no one uses the CRM..."
                      className="input-industrial resize-none"
                    />
                  </div>
                </motion.div>
              )}

              {/* ============ STEP 3 · FRICTION ============ */}
              {step === 2 && (
                <motion.div key="s2" variants={stepVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
                  <div>
                    <label className="mono-label block mb-3">01 · The Primary Bottleneck</label>
                    <p className="text-nex-chrome text-sm mb-3">
                      One paragraph — what&apos;s the <em>one thing</em> your team keeps screwing up on paper, in text messages, or across spreadsheets?
                    </p>
                    <textarea
                      data-testid="input-bottleneck"
                      value={form.bottleneck}
                      onChange={update("bottleneck")}
                      rows={5}
                      placeholder="Example: our field techs text photos of what they pulled from the truck, someone in the shop retypes it into a spreadsheet, and by end of week we're always short on copper..."
                      className="input-industrial resize-none"
                    />
                    <div className="mono-body text-[10px] mt-2">
                      {form.bottleneck.length < 15 ? `${15 - form.bottleneck.length} more chars to unlock next step` : "✓ ready"}
                    </div>
                  </div>
                  <div>
                    <label className="mono-label block mb-3">02 · Secondary Bottleneck <span className="text-nex-muted">(optional)</span></label>
                    <textarea
                      data-testid="input-secondary-bottleneck"
                      value={form.secondary_bottleneck}
                      onChange={update("secondary_bottleneck")}
                      rows={2}
                      placeholder="A second recurring headache — invoicing delays, missed follow-ups, etc."
                      className="input-industrial resize-none"
                    />
                  </div>
                  <div>
                    <label className="mono-label block mb-3">03 · Where does data slip through the cracks? <span className="text-nex-muted">(optional)</span></label>
                    <input
                      data-testid="input-data-leakage"
                      type="text"
                      value={form.data_leakage}
                      onChange={update("data_leakage")}
                      placeholder="e.g. between truck & shop, from quote to invoice, from tech notes to invoice..."
                      className="input-industrial"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="mono-label block mb-3">04 · How often does this happen?</label>
                      <div className="flex flex-wrap gap-2">
                        {FREQ_OPTIONS.map((f) => (
                          <button
                            key={f}
                            type="button"
                            data-testid={`freq-${f.split(" ")[0].toLowerCase()}`}
                            onClick={() => setValue("bottleneck_frequency", f)}
                            className={`px-3 py-2 text-[13px] border transition-colors ${
                              form.bottleneck_frequency === f
                                ? "border-nex-gold text-nex-gold bg-nex-gold/10"
                                : "border-white/10 text-nex-silver hover:border-white/30"
                            }`}
                          >
                            {f}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="mono-label block mb-3">05 · Cost estimate <span className="text-nex-muted">(optional)</span></label>
                      <input
                        data-testid="input-revenue-impact"
                        type="text"
                        value={form.revenue_impact}
                        onChange={update("revenue_impact")}
                        placeholder="~$800/wk in duplicate orders · 5hrs/wk admin..."
                        className="input-industrial"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ============ STEP 4 · GOALS ============ */}
              {step === 3 && (
                <motion.div key="s3" variants={stepVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
                  <div>
                    <label className="mono-label block mb-3">01 · What does success look like in 90 days?</label>
                    <textarea
                      data-testid="input-success-90"
                      value={form.success_90_days}
                      onChange={update("success_90_days")}
                      rows={4}
                      placeholder="e.g. Zero inventory surprises, one source of truth between field and shop, invoices out same-day, owner gets a Monday KPI report automatically..."
                      className="input-industrial resize-none"
                    />
                  </div>
                  <div>
                    <label className="mono-label block mb-3">02 · Which KPIs matter most? <span className="text-nex-muted">(pick any)</span></label>
                    <MultiChip
                      options={KPI_OPTIONS}
                      value={form.key_kpis}
                      onToggle={toggleIn("key_kpis")}
                      testPrefix="kpi"
                    />
                  </div>
                  <div>
                    <label className="mono-label block mb-3">03 · 12-month growth goal <span className="text-nex-muted">(optional)</span></label>
                    <textarea
                      data-testid="input-growth-12mo"
                      value={form.growth_goals_12mo}
                      onChange={update("growth_goals_12mo")}
                      rows={2}
                      placeholder="e.g. Grow crew from 6 to 12 · Add second location · Double revenue · Open a retail arm..."
                      className="input-industrial resize-none"
                    />
                  </div>
                  <div>
                    <label className="mono-label block mb-3">04 · Any must-have integrations? <span className="text-nex-muted">(optional)</span></label>
                    <input
                      data-testid="input-integrations"
                      type="text"
                      value={form.required_integrations}
                      onChange={update("required_integrations")}
                      placeholder="e.g. QuickBooks, Stripe, Google Workspace, DocuSign, our supplier's API..."
                      className="input-industrial"
                    />
                  </div>
                </motion.div>
              )}

              {/* ============ STEP 5 · TIMING + CONTACT ============ */}
              {step === 4 && (
                <motion.div key="s4" variants={stepVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
                  <div>
                    <label className="mono-label block mb-3">01 · When do you want this solved?</label>
                    <ButtonGrid options={TIMELINES} value={form.timeline} onChange={(v) => setValue("timeline", v)} testPrefix="timeline" />
                  </div>
                  <div>
                    <label className="mono-label block mb-3">02 · Budget Tier <span className="text-nex-muted">(optional)</span></label>
                    <div className="space-y-2">
                      {BUDGET_TIERS.map((bt) => (
                        <button
                          key={bt}
                          type="button"
                          data-testid={`budget-${bt.split(" ")[1]?.toLowerCase() || "unsure"}`}
                          onClick={() => setValue("budget_tier", bt)}
                          className={`w-full text-left px-4 py-3 text-sm border transition-colors ${
                            form.budget_tier === bt
                              ? "border-nex-gold text-nex-gold bg-nex-gold/10"
                              : "border-white/10 text-nex-silver hover:border-white/30"
                          }`}
                        >
                          {bt}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="mono-label block mb-3">03 · Preferred engagement <span className="text-nex-muted">(optional)</span></label>
                      <div className="flex flex-wrap gap-2">
                        {ENGAGEMENT_OPTIONS.map((e) => (
                          <button
                            key={e}
                            type="button"
                            data-testid={`engagement-${e.split(" ")[0].toLowerCase()}`}
                            onClick={() => setValue("preferred_engagement", e)}
                            className={`px-3 py-2 text-[13px] border transition-colors ${
                              form.preferred_engagement === e
                                ? "border-nex-gold text-nex-gold bg-nex-gold/10"
                                : "border-white/10 text-nex-silver hover:border-white/30"
                            }`}
                          >
                            {e}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="mono-label block mb-3">04 · Best time to schedule a call <span className="text-nex-muted">(optional)</span></label>
                      <div className="flex flex-wrap gap-2">
                        {CALL_TIMES.map((c) => (
                          <button
                            key={c}
                            type="button"
                            data-testid={`calltime-${c.split(" ")[0].toLowerCase()}`}
                            onClick={() => setValue("best_call_time", c)}
                            className={`px-3 py-2 text-[13px] border transition-colors ${
                              form.best_call_time === c
                                ? "border-nex-gold text-nex-gold bg-nex-gold/10"
                                : "border-white/10 text-nex-silver hover:border-white/30"
                            }`}
                          >
                            {c}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="metal-divider !my-6" />
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="mono-label block mb-3">05 · Full Name</label>
                      <input
                        data-testid="input-full-name"
                        type="text"
                        value={form.full_name}
                        onChange={update("full_name")}
                        placeholder="e.g. William O. Smith"
                        className="input-industrial"
                      />
                    </div>
                    <div>
                      <label className="mono-label block mb-3">06 · Phone <span className="text-nex-muted">(optional)</span></label>
                      <input
                        data-testid="input-phone"
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="+1 555 000 0000"
                        className="input-industrial"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mono-label block mb-3">07 · Business Email</label>
                    <input
                      data-testid="input-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@yourbusiness.com"
                      className="input-industrial"
                    />
                  </div>
                  <div className="mono-body text-[11px] leading-relaxed border-l-2 border-nex-gold/40 pl-4">
                    &gt; By submitting, William personally reviews your Engine Brief. If it&apos;s a fit, he&apos;ll send you an acceptance link with the consultation fee — then we begin your prototype.
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Nav */}
          <div className="flex items-center justify-between mt-10 pt-8 border-t border-white/10">
            <button
              onClick={back}
              disabled={step === 0 || submitting}
              data-testid="audit-back-btn"
              className="tick-btn-secondary disabled:opacity-30 disabled:pointer-events-none"
            >
              <ArrowLeft size={14} /> Back
            </button>

            {step < STEPS.length - 1 ? (
              <button
                onClick={next}
                disabled={!canAdvance}
                data-testid="audit-next-btn"
                className="tick-btn-primary disabled:opacity-40 disabled:pointer-events-none"
              >
                Next Step <ArrowRight size={16} />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={!canAdvance || submitting}
                data-testid="audit-submit-btn"
                className="tick-btn-primary disabled:opacity-40 disabled:pointer-events-none"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Submitting...
                  </>
                ) : (
                  <>
                    Submit Engine Brief <Sparkles size={16} />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </FormOuter>
  );
};

export default FrictionAudit;
